import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) setError('Missing reset token. Please request a new reset link.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center px-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Reset Password</CardTitle>
          <CardDescription>Set a new password for your Agent Portal account.</CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
              <p className="font-semibold">Password updated!</p>
              <p className="text-sm text-gray-600 mt-2">Sign in with your new password.</p>
              <Button
                onClick={() => navigate('/agent-portal')}
                className="mt-4 w-full bg-[#BCA480] hover:bg-[#a89470] text-white"
                data-testid="go-to-login"
              >
                Sign In
              </Button>
            </div>
          ) : error && !pw ? (
            <div className="text-center py-4">
              <AlertCircle className="w-12 h-12 mx-auto text-red-600 mb-3" />
              <p className="text-sm text-red-700">{error}</p>
              <Link to="/forgot-password" className="text-sm text-[#BCA480] hover:underline block mt-4">
                Request a new reset link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button
                type="submit"
                className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white"
                disabled={loading || !token}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Updating…' : 'Update Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
